import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Route } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { ConfigManager, SITE_TYPE } from '~/managers/ConfigManager'
import favicon from '~/assets/straitsXFavicon.png'
import { hasPermission } from '~/permission'
import AccessDenied from '~/components/AccessDenied'
import { selectFeatureMatrixFactors as bizFeatureMatrix } from '~/biz/store/route/selectors'
import { selectFeatureMatrixFactors as xidrBizFeatureMatrix } from '~/xidrBiz/store/route/selectors'
import { FeatureMatrixSelectorsType } from '~/types'

import Locked from '~/app/pages/Locked'

import { selectFlipperWithId } from '~/store/flippers/selectors'
import { FLIPPER_KEYS } from '~/store/flippers/const'

import { RootState as BizRootStore } from '~/biz/store/types'
import { useResponsive } from '~/hooks/useResponsive'

import { selectUserEmail } from '~/app/store/userInfo/selectors'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PrivateRoute = (route: any) => {
  const {
    documentTitle,
    permission,
    path,
    routes,
    isAccountLocked,
    isLockedFeatureOn,
    isPeriodicReviewRequired,
  } = route

  let siteName = 'StraitsX'
  if (ConfigManager.isXIDRSite()) siteName += ' ID'
  if (ConfigManager.isBizSite()) siteName += ' Business'

  const { isMobileView } = useResponsive()

  const email =
    ConfigManager.getSiteType() === SITE_TYPE.APP
      ? useSelector(selectUserEmail)
      : useSelector((state: BizRootStore) => state.route.user.email)

  const flipperEnableZendeskAnswerbot = useSelector(
    selectFlipperWithId(FLIPPER_KEYS.ZENDESK_ANSWERBOT, email)
  )

  const flipperEnableFreshdeskAnswerbot = useSelector(
    selectFlipperWithId(FLIPPER_KEYS.FRESHDESK_ANSWERBOT, email)
  )

  let title: string = ''
  if (documentTitle) {
    const { name, withSiteTypePrefix = false, withSiteTypeSuffix = true } = documentTitle
    title = `
      ${withSiteTypePrefix ? siteName + ' ' : ''}
      ${name}
      ${withSiteTypeSuffix ? ' | ' + siteName : ''}
    `
  } else {
    title = `${siteName}, Internet Banking Payments Made Easy`
  }

  if (
    isAccountLocked &&
    isPeriodicReviewRequired &&
    isLockedFeatureOn &&
    ConfigManager.isXSGDPersonalSite()
  ) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
          <link rel="icon" type="image/png" href={favicon} />
        </Helmet>
        <Route component={Locked} path={path} />
      </>
    )
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!isMobileView) return

      const scrollPosition = window.scrollY + window.innerHeight
      const documentHeight = document.documentElement.scrollHeight
      const distanceFromBottom = documentHeight - scrollPosition
      const selectors = ['#launcher', '[title="Close message"]', '[title="Message from company"]']

      selectors.forEach(selector => {
        const el = document.querySelector(selector)
        if (el && el instanceof HTMLElement) {
          el.style.display = distanceFromBottom < 200 ? 'none' : ''
        }
      })
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMobileView])

  if (permission) {
    const featureMatrix: FeatureMatrixSelectorsType = ConfigManager.isXSGDSite()
      ? useSelector(bizFeatureMatrix)
      : useSelector(xidrBizFeatureMatrix)

    if (!hasPermission(permission, featureMatrix)) {
      return (
        <>
          <Helmet>
            <title>{title}</title>
            <link rel="icon" type="image/png" href={favicon} />
          </Helmet>
          <Route component={AccessDenied} path={path} />
        </>
      )
    }
  }

  return (
    <Route
      sensitive
      path={path}
      render={props => (
        <>
          <Helmet>
            <title>{title}</title>
            <link rel="icon" type="image/png" href={favicon} />
            {flipperEnableZendeskAnswerbot?.enabled && (
              <script
                id="ze-snippet"
                src="https://static.zdassets.com/ekr/snippet.js?key=1d1bbbc7-7faf-4642-a770-f0fe0e2018d6"
              />
            )}
            {flipperEnableFreshdeskAnswerbot?.enabled && (
              <script src="//fw-cdn.com/12585365/4979242.js" chat="true"></script>
            )}
          </Helmet>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <route.component {...props} routes={routes} />
        </>
      )}
    />
  )
}

export default PrivateRoute
